<section
  class="workflow-draft-graph-preview"
  [class.workflow-draft-graph-preview--contextual]="contextualInspector"
  [attr.aria-label]="contextualInspector ? 'Workflow map editor' : 'Workflow map preview'"
  aria-describedby="workflow-draft-graph-preview-description"
  data-testid="workflow-draft-graph-preview"
>
  <p id="workflow-draft-graph-preview-description" class="visually-hidden">
    <ng-container *ngIf="contextualInspector; else previewDescription">
      Arrange the workflow by dragging stages. Positions are remembered for this draft in this browser.
      Use the Steps list to inspect a step, then choose Open step editor to edit it, or select a route to inspect it. The
      optional responsibility view shows saved queue and role guidance; it does not assign work or
      grant access. Server validation remains authoritative for whether the saved draft is valid or
      executable.
    </ng-container>
    <ng-template #previewDescription>
      Read-only compact view of the authored stage path and its decision requirements. The optional
      responsibility view shows saved queue and role guidance; it does not assign work or grant
      access. Server validation remains authoritative for whether the saved draft is valid or
      executable.
    </ng-template>
  </p>

  <p
    *ngIf="sourceLoading && draft"
    class="workflow-draft-graph-preview__notice"
    role="status"
    aria-live="polite"
    data-testid="workflow-graph-source-loading"
  >
    A draft revision is loading. This preview still reflects {{ draft.draftCode }} saved revision
    {{ draft.revision }} and its current local working document until that load succeeds.
  </p>

  <p
    *ngIf="recoveryCopyPending"
    class="workflow-draft-graph-preview__warning"
    role="status"
    aria-live="polite"
    data-testid="workflow-graph-recovery-copy"
  >
    <ng-container *ngIf="recoveryLatestLoaded; else conflictCopyStillShown">
      The preserved conflict copy is not shown. This graph reflects the reloaded working document
      until you choose Review combined draft.
    </ng-container>
    <ng-template #conflictCopyStillShown>
      This graph still reflects the local document preserved after the save conflict. Reload the
      latest saved revision before deciding whether to restore or discard that copy.
    </ng-template>
  </p>

  <ng-container *ngIf="graph() as currentGraph">
    <p
      *ngIf="hasPreviewWarnings()"
      class="workflow-draft-graph-preview__warning"
      role="status"
      aria-live="polite"
      data-testid="workflow-graph-structural-warning"
    >
      The preview found {{ currentGraph.invalidElementCount }} invalid or unresolved graph
      {{ currentGraph.invalidElementCount === 1 ? 'element' : 'elements' }} and
      {{ currentGraph.malformedEntryCount }} malformed nested
      {{ currentGraph.malformedEntryCount === 1 ? 'entry' : 'entries' }}. Placeholder items keep
      the document inspectable; run server validation for authoritative diagnostics.
    </p>

    <phms-workflow-read-only-graph
      [graph]="currentGraph"
      [layoutStorageKey]="contextualInspector && draft ? 'draft:' + draft.id : null"
      [contextualInspector]="contextualInspector"
      [inspectorStandalone]="inspectorStandalone"
      [selectionControlled]="selectionControlled"
      [selectedRendererElementId]="selectedRendererElementId"
      visualAriaLabel="Workflow stage and decision diagram for the current draft"
      (editNodeRequested)="requestNodeEdit($event)"
      (editEdgeRequested)="requestEdgeEdit($event)"
      (selectionRequested)="requestSelection($event)"
      (decisionGateRequested)="requestDecisionGate($event)"
      (decisionScopeRequested)="requestDecisionScope($event)"
    >
      <ng-container ngProjectAs="[workflowGraphToolbarActions]">
        <ng-content select="[workflowGraphToolbarActions]"></ng-content>
      </ng-container>
      <ng-container ngProjectAs="[workflowGraphInspector]">
        <ng-content select="[workflowGraphInspector]"></ng-content>
      </ng-container>
    </phms-workflow-read-only-graph>
  </ng-container>

  <p
    *ngIf="!graph()"
    class="workflow-draft-graph-preview__empty"
    role="status"
    aria-live="polite"
  >
    Open a draft to preview its workflow diagram.
  </p>
</section>
